// Beta engineering surfaces (currently the publish-status panel — see
// PublishStatusPanel.tsx) shouldn't be in front of ordinary users by default, but they
// are genuinely useful when chasing a report of "my recording got stuck" — better to have a
// deliberate, memorable way back in than to delete the panel outright. Same shape as Android's
// build-number tap: unlocked by tapping a specific spot several times, not exposed as a menu
// item, persisted so it doesn't have to be re-unlocked every visit.
const STORAGE_KEY = "grackle.devMode";

type Listener = (on: boolean) => void;
const listeners = new Set<Listener>();

function read(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

let current = read();

// `?debug=1` is the documented escape hatch for anyone who can't discover (or doesn't want to
// do) the tap sequence — e.g. a support engineer sent a repro link. It only ever turns dev mode
// on; the tap target is still how it's turned back off.
try {
  if (new URLSearchParams(window.location.search).get("debug") === "1") {
    current = true;
    localStorage.setItem(STORAGE_KEY, "1");
  }
} catch {
  // SSR/no-window or a locked-down localStorage: dev mode just stays off.
}

export function isDevMode(): boolean {
  return current;
}

export function setDevMode(on: boolean): void {
  current = on;
  try {
    if (on) localStorage.setItem(STORAGE_KEY, "1");
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // best-effort persistence only
  }
  listeners.forEach((l) => l(current));
}

export function toggleDevMode(): boolean {
  setDevMode(!current);
  return current;
}

/** Returns an unsubscribe function, same shape as this codebase's other subscribe*() helpers. */
export function subscribeDevMode(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
